import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  CheckCircle,
  Zap,
  Database,
  Cloud,
  TrendingUp,
} from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import GetStartedDialog from "@/components/GetStartedDialog";
import s4hanaImage from "@/assets/sap-s4hana-dashboard.jpg";

const SAPS4HANADetail = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: Database,
      title: "In-Memory Computing",
      description:
        "HANA database enables real-time analytics and instant insights with lightning-fast data processing",
    },
    {
      icon: Cloud,
      title: "Cloud & On-Premise",
      description:
        "Flexible deployment options including cloud, on-premise, or hybrid models to suit your needs",
    },
    {
      icon: Zap,
      title: "Intelligent Technologies",
      description:
        "Built-in AI, machine learning, and advanced analytics for smarter business decisions",
    },
    {
      icon: TrendingUp,
      title: "Simplified Data Model",
      description:
        "Streamlined architecture reduces complexity and improves system performance by up to 50%",
    },
  ];

  const modules = [
    {
      name: "Finance (FI/CO)",
      capabilities: [
        "Real-time financial reporting and analytics",
        "Universal journal for simplified financial processes",
        "Advanced payment and cash management",
        "Embedded predictive analytics",
      ],
    },
    {
      name: "Supply Chain (MM/PP/QM)",
      capabilities: [
        "Integrated supply chain planning and execution",
        "Real-time inventory and warehouse management",
        "Advanced production planning and scheduling",
        "Quality management and inspection processes",
      ],
    },
    {
      name: "Sales & Distribution (SD)",
      capabilities: [
        "Omnichannel order management",
        "Real-time pricing and availability",
        "Advanced shipping and logistics",
        "Customer relationship integration",
      ],
    },
    {
      name: "Asset Management (PM)",
      capabilities: [
        "Predictive maintenance and IoT integration",
        "Mobile asset management",
        "Work order management",
        "Equipment lifecycle tracking",
      ],
    },
  ];

  const benefits = [
    "In-memory computing eliminates aggregates and indexes - up to 10x faster queries",
    "Universal Journal combines FI and CO into single data structure",
    "Embedded analytics with CDS views eliminate need for separate BW system",
    "Simplified data model reduces database footprint by 50-70%",
    "SAP Fiori apps replace classic transactions with modern responsive UI",
    "Direct integration with S/4HANA Cloud and BTP services",
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <div className="pt-20 pb-16">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-primary/10 via-secondary/5 to-background py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Button
              variant="ghost"
              onClick={() => window.history.back()}
              className="mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            <div className="space-y-4">
              <Badge className="bg-primary text-white hover:bg-primary/90">
                Next-Generation ERP
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                SAP S/4HANA
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl">
                SAP's next-generation ERP built on the HANA in-memory database.
                Replaces traditional SAP ECC with a simplified data model,
                real-time processing, and Fiori UX. Available as Cloud,
                On-Premise, or Hybrid deployment.
              </p>
            </div>
          </div>
        </div>

        {/* Hero Image */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="rounded-xl overflow-hidden shadow-2xl">
            <img
              src={s4hanaImage}
              alt="SAP S/4HANA Dashboard showing real-time analytics and intelligent ERP interface"
              className="w-full h-auto"
            />
          </div>
        </div>

        {/* Key Features */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Key Technical Capabilities
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Built on SAP HANA database with column-store architecture and
              advanced optimization
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow bg-gradient-card border-0">
                <CardHeader>
                  <feature.icon className={`h-12 w-12 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mb-4`} />
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Modules & Capabilities */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Core Modules</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Comprehensive business process coverage with integrated modules
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {modules.map((module, idx) => (
              <Card key={idx} className="bg-gradient-card border-0">
                <CardHeader>
                  <CardTitle className="text-xl flex items-center">
                    <CheckCircle className={`h-6 w-6 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mr-3`} />
                    {module.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {module.capabilities.map((capability, cIdx) => (
                      <li key={cIdx} className="flex items-start">
                        <span className={`inline-block w-2 h-2 rounded-full ${cIdx % 2 === 0 ? 'bg-primary shadow-glow' : 'bg-secondary shadow-glow-secondary'} mt-2 mr-3 flex-shrink-0`} />
                        <span className="text-muted-foreground">
                          {capability}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Business Benefits */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-gradient-to-br from-primary/10 via-background to-secondary/10">
            <CardHeader>
              <CardTitle className="text-2xl">Business Benefits</CardTitle>
              <p className="text-muted-foreground mt-2">
                Measurable impact across your organization
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start">
                    <CheckCircle className={`h-5 w-5 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mr-3 mt-0.5 flex-shrink-0`} />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="bg-gradient-to-r from-primary to-secondary text-white border-0 shadow-glow">
            <CardContent className="p-8 text-center">
              <h2 className="text-3xl font-bold mb-4">
                Ready to Transform with SAP S/4HANA?
              </h2>
              <p className="text-xl mb-6 text-white/90">
                Let our SAP experts guide your digital transformation journey
              </p>
              <GetStartedDialog />
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SAPS4HANADetail;
